import { Link } from 'react-router-dom';
import Breadcrumbs from '@/components/base/Breadcrumbs';
import SectionHeading from '@/components/base/SectionHeading';
import Reveal from '@/components/base/Reveal';
import { guideSteps, accessTips } from '@/mocks/guideSteps';
import { useSeo } from '@/hooks/useSeo';

const SITE_URL = 'https://hnbhcpa.cn';

export default function GuidePage() {
  useSeo({
    title: '爆料专区访问指南 | 首页入口与网址使用方法',
    description:
      '爆料专区访问指南：分步说明爆料专区首页入口、网址网页与热门讨论区的使用方法，附新手避坑要点与信息判断技巧，帮助你快速上手。',
    keywords: '爆料专区访问指南,爆料专区入口,爆料专区首页,爆料专区网址,爆料专区网页',
    path: '/guide',
    schema: [
      {
        '@context': 'https://schema.org',
        '@type': 'HowTo',
        name: '爆料专区访问指南',
        description: '分步说明如何找到并使用爆料专区的首页入口、网址与热门讨论区。',
        step: guideSteps.map((s) => ({
          '@type': 'HowToStep',
          position: Number(s.step),
          name: s.title,
          text: s.desc,
        })),
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: `${SITE_URL}/` },
          { '@type': 'ListItem', position: 2, name: '访问指南', item: `${SITE_URL}/guide` },
        ],
      },
    ],
  });

  return (
    <main className="pt-16 md:pt-20">
      <section className="bg-background-100 border-b border-background-200">
        <div className="w-full px-4 md:px-8 lg:px-12 py-10 md:py-14">
          <Breadcrumbs items={[{ label: '首页', path: '/' }, { label: '访问指南' }]} />
          <h1 className="font-heading mt-5 text-3xl md:text-5xl font-bold text-foreground-950 leading-tight">
            爆料专区访问指南
          </h1>
          <p className="mt-4 max-w-3xl text-sm md:text-base leading-relaxed text-foreground-600">
            第一次接触爆料专区，最容易卡在「从哪里进、看什么、怎么判断」这三件事上。下面用四个步骤把
            <strong className="font-medium text-foreground-900"> 首页入口、网址网页与热门讨论区 </strong>
            串成一条清晰路线，跟着走一遍基本就熟悉了。
          </p>
        </div>
      </section>

      <section className="w-full px-4 md:px-8 lg:px-12 py-12 md:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12">
          <div className="lg:col-span-8">
            <SectionHeading eyebrow="Step by Step" title="四步走完访问全流程" />
            <div className="mt-10 space-y-4">
              {guideSteps.map((step, index) => (
                <Reveal key={step.id} delay={index * 60}>
                  <article className="relative bg-background-50 border border-background-200 rounded-lg p-5 md:p-7">
                    <div className="flex flex-col sm:flex-row sm:items-start gap-4">
                      <span className="font-heading shrink-0 w-14 h-14 rounded-lg bg-primary-500 text-background-50 flex items-center justify-center text-xl font-bold">
                        {step.step}
                      </span>
                      <div className="flex-1">
                        <div className="flex flex-wrap items-center gap-3">
                          <h3 className="font-heading text-lg md:text-xl font-semibold text-foreground-950">
                            {step.title}
                          </h3>
                          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-800 text-xs whitespace-nowrap">
                            <i className="ri-timer-line" />
                            {step.duration}
                          </span>
                        </div>
                        <p className="mt-3 text-sm md:text-base leading-relaxed text-foreground-600">
                          {step.desc}
                        </p>
                        <ul className="mt-4 flex flex-wrap gap-2">
                          {step.points.map((point) => (
                            <li
                              key={point}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-background-100 text-xs text-foreground-700"
                            >
                              <i className="ri-check-line text-accent-600" />
                              {point}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </article>
                </Reveal>
              ))}
            </div>
          </div>

          <aside className="lg:col-span-4">
            <div className="lg:sticky lg:top-28 space-y-6">
              <div className="bg-secondary-950 rounded-lg p-6 text-secondary-100">
                <h2 className="font-heading text-lg font-semibold text-background-50">新手避坑要点</h2>
                <ul className="mt-5 space-y-4">
                  {accessTips.map((tip) => (
                    <li key={tip.id} className="flex items-start gap-3">
                      <span className="w-9 h-9 shrink-0 rounded-md bg-secondary-900 text-accent-400 flex items-center justify-center text-lg">
                        <i className={tip.icon} />
                      </span>
                      <span>
                        <span className="block text-sm font-medium text-background-50">{tip.title}</span>
                        <span className="mt-1 block text-xs leading-relaxed text-secondary-300">
                          {tip.desc}
                        </span>
                      </span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-accent-50 border border-accent-200 rounded-lg p-6">
                <h2 className="font-heading text-base font-semibold text-foreground-950">
                  还没找到想要的入口？
                </h2>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                  热门话题页支持按分类与关键词筛选，能更快定位你关心的方向。
                </p>
                <Link
                  to="/topics"
                  className="mt-4 inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-foreground-950 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  前往热门话题
                  <i className="ri-arrow-right-line" />
                </Link>
              </div>
            </div>
          </aside>
        </div>
      </section>
    </main>
  );
}